import React, { useMemo } from "react";
import styles from "./DurationInputField.module.css";
import classnames from "classnames";

interface Props {
    title: string;
    valueSeconds: number;
    onChangeSeconds: (totalSeconds: number) => void;
    disabled?: boolean;
    required?: boolean;
    errorMessage?: string;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export const DurationInputField: React.FC<Props> = ({
    title,
    valueSeconds,
    onChangeSeconds,
    disabled,
    required,
    errorMessage
}) => {
    const { hours, minutes, seconds } = useMemo(() => {
        const safe = Number.isFinite(valueSeconds) && valueSeconds >= 0 ? valueSeconds : 0;
        const h = Math.floor(safe / 3600);
        const m = Math.floor((safe % 3600) / 60);
        const s = Math.floor(safe % 60);
        return { hours: h, minutes: m, seconds: s };
    }, [valueSeconds]);

    const update = (next: { hours?: number; minutes?: number; seconds?: number }) => {
        const h = clamp(next.hours ?? hours, 0, 9999); // reasonably large cap
        const m = clamp(next.minutes ?? minutes, 0, 59);
        const s = clamp(next.seconds ?? seconds, 0, 59);
        onChangeSeconds(h * 3600 + m * 60 + s);
    };

    const format2 = (n: number) => n.toString().padStart(2, "0");

    return (
        <div className={styles.wrapper}>
            <div className={styles.titleWrapper}>
                <span className={classnames(styles.title, disabled && styles.disabledTitle)}>{title}</span>
                {(required && !disabled) && <span className={styles.asterisk}>*</span>}
            </div>

            <div className={classnames(styles.inputGroup, disabled && styles.disabled)}>
                <div className={styles.segment}>
                    <div className={styles.segmentLabel}>hours</div>
                    <input
                        type="number"
                        min={0}
                        value={disabled ? "" : hours}
                        onChange={(e) => update({ hours: Number(e.target.value || 0) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={styles.segmentInput}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.separator}>:</div>

                <div className={styles.segment}>
                    <div className={styles.segmentLabel}>minutes</div>
                    <input
                        type="number"
                        min={0}
                        max={59}
                        value={disabled ? "" : format2(minutes)}
                        onChange={(e) => update({ minutes: Number(e.target.value || 0) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={styles.segmentInput}
                        disabled={disabled}
                    />
                </div>

                <div className={styles.separator}>:</div>

                <div className={styles.segment}>
                    <div className={styles.segmentLabel}>seconds</div>
                    <input
                        type="number"
                        min={0}
                        max={59}
                        value={disabled ? "" : format2(seconds)}
                        onChange={(e) => update({ seconds: Number(e.target.value || 0) })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={styles.segmentInput}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div style={{visibility: `${!errorMessage ? "hidden" : "visible"}`}} className={styles.errorWrapper}>
                <img src={"/alertIcon.svg"} alt={"alert icon"}/>
                <span className={styles.errorMessage}>{errorMessage}</span>
            </div>
        </div>
    );
}; 
